import React, { useState } from 'react';
import { Calculator, Minus, Plus } from 'lucide-react';
import { formatInr, formatSignedPercent } from '../../../utils/gmpBoard';

const MAX_LOTS = 999;
const QUICK_LOTS = [1, 2, 5, 10];

export const CompanyGainEstimator = ({ row }) => {
  const [lots, setLots] = useState(1);

  const missing = [];
  if (!row.hasGmp) missing.push('a grey market quote');
  if (row.price === null) missing.push('the price band');
  if (row.lotSize === null) missing.push('the lot size');

  const setLotCount = (value) => {
    const next = Math.round(Number(value));
    setLots(Number.isFinite(next) ? Math.min(MAX_LOTS, Math.max(1, next)) : 1);
  };

  const header = (
    <div className="flex items-center justify-between gap-2">
      <h2 className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
        <Calculator className="w-4 h-4 text-[#008080]" />
        Listing Gain Estimator
      </h2>
      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-teal-50 text-[#008080] border border-teal-200">
        This IPO
      </span>
    </div>
  );

  if (missing.length) {
    return (
      <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        {header}
        <p className="text-xs text-slate-500 mt-3 leading-relaxed">
          An estimate needs {missing.join(', ').replace(/, ([^,]*)$/, ' and $1')}, which the source hasn&apos;t
          published for this issue yet.
        </p>
      </section>
    );
  }

  const shares = lots * row.lotSize;
  const investment = shares * row.price;
  const gain = shares * row.gmp;
  const listingValue = investment + gain;
  const roi = investment ? (gain / investment) * 100 : 0;
  const isLoss = gain < 0;

  const results = [
    { label: 'Shares applied', value: shares.toLocaleString('en-IN') },
    { label: 'Investment', value: formatInr(investment) },
    { label: 'Est. value on listing', value: formatInr(listingValue) },
  ];

  return (
    <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
      {header}

      <label htmlFor="gmp-estimator-lots" className="block mt-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
        Number of lots
      </label>
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setLotCount(lots - 1)}
          disabled={lots <= 1}
          aria-label="Remove a lot"
          className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-[#008080] hover:text-[#008080] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
        >
          <Minus className="w-4 h-4" />
        </button>
        <input
          id="gmp-estimator-lots"
          type="number"
          inputMode="numeric"
          min="1"
          max={MAX_LOTS}
          value={lots}
          onChange={(e) => setLotCount(e.target.value)}
          className="w-full h-9 px-3 text-center bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-800 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
        />
        <button
          type="button"
          onClick={() => setLotCount(lots + 1)}
          disabled={lots >= MAX_LOTS}
          aria-label="Add a lot"
          className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-[#008080] hover:text-[#008080] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="flex gap-1.5 mt-2">
        {QUICK_LOTS.map((count) => (
          <button
            key={count}
            type="button"
            onClick={() => setLotCount(count)}
            aria-pressed={lots === count}
            className={`flex-1 py-1 rounded-md text-[11px] font-bold border transition-colors ${
              lots === count
                ? 'bg-[#008080] border-[#008080] text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            {count} {count === 1 ? 'lot' : 'lots'}
          </button>
        ))}
      </div>

      <dl className="mt-4 space-y-2">
        {results.map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between text-xs">
            <dt className="text-slate-500">{label}</dt>
            <dd className="font-bold text-slate-800">{value}</dd>
          </div>
        ))}
      </dl>

      <div
        className={`mt-4 rounded-xl p-4 text-white bg-gradient-to-br ${
          isLoss ? 'from-rose-600 to-rose-700' : 'from-emerald-600 to-teal-700'
        }`}
      >
        <div className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
          Estimated listing {isLoss ? 'loss' : 'gain'}
        </div>
        <div className="flex items-baseline justify-between gap-2 mt-0.5">
          <span className="text-2xl font-extrabold tracking-tight">
            {isLoss ? '−' : ''}
            {formatInr(Math.abs(gain))}
          </span>
          <span className="text-xs font-bold">{formatSignedPercent(roi, 1)}</span>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        Based on the latest GMP at the {formatInr(row.price)} upper band. Excludes taxes and charges; actual listing
        prices can differ.
      </p>
    </section>
  );
};

export default CompanyGainEstimator;
